import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Sparkles,
  Target,
  Heart,
  MessageCircle,
  TrendingUp,
  Users,
  AlertCircle,
  RefreshCw,
  Edit,
  Save,
  CheckCircle2,
  XCircle,
  Star,
  Activity,
} from 'lucide-react';
import { sendChatMessage } from '../../../api/AI/chat';
import type { Employee, ReviewData } from '../index';

interface NarrativeCompilationPanelProps {
  employee: Employee;
  reviewData: ReviewData;
  onNarrativeUpdate: (narrative: NarrativeData) => void;
}

interface NarrativeData {
  summary: string;
  goalsAchieved: string[];
  goalsMissed: string[];
  impactHighlights: string[];
  feedbackThemes: string[];
  behaviorPatterns: string[];
  teamContext: string;
}

const NarrativeCompilationPanel = ({
  employee,
  reviewData,
  onNarrativeUpdate,
}: NarrativeCompilationPanelProps) => {
  const { t } = useTranslation();
  const [narrative, setNarrative] = useState<NarrativeData | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedNarrative, setEditedNarrative] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Parse snapshot JSON summary fields for use in prompts and fallback narrative
  const snapshot = reviewData.snapshot;
  let goalsData = { total: 0, completed: 0, onTrack: 0, atRisk: 0, overdue: 0 };
  let checkInsData = { total: 0, withContent: 0, avgQuality: 0 };
  let recognitionData = { received: 0, given: 0 };
  let strengthsData: { topSkills: { skillName: string; proficiency: string; endorsementCount: number }[] } = { topSkills: [] };

  try { if (snapshot?.goalsSummary) goalsData = JSON.parse(snapshot.goalsSummary); } catch { /* ignore parse errors */ }
  try { if (snapshot?.checkInsSummary) checkInsData = JSON.parse(snapshot.checkInsSummary); } catch { /* ignore parse errors */ }
  try { if (snapshot?.recognitionSummary) recognitionData = JSON.parse(snapshot.recognitionSummary); } catch { /* ignore parse errors */ }
  try { if (snapshot?.strengthsSummary) strengthsData = JSON.parse(snapshot.strengthsSummary); } catch { /* ignore parse errors */ }

  const fallbackNarrative: NarrativeData = {
    summary: `Based on the performance data for ${employee.name}, the overall score is ${snapshot?.overallScore != null ? Math.round(snapshot.overallScore) + '%' : 'not yet calculated'}. This review covers the ${snapshot?.period || 'current'} period.`,
    goalsAchieved: goalsData.completed > 0 ? [`Completed ${goalsData.completed} of ${goalsData.total} goals`] : ['No completed goals data available'],
    goalsMissed: goalsData.atRisk > 0 || goalsData.overdue > 0 ? [`${goalsData.atRisk} at-risk and ${goalsData.overdue} overdue goals require attention`] : [],
    impactHighlights: recognitionData.received > 0 ? [`Received ${recognitionData.received} recognitions from peers`] : ['No recognition data available'],
    feedbackThemes: checkInsData.total > 0 ? [`Completed ${checkInsData.total} check-ins with average quality of ${checkInsData.avgQuality}%`] : ['No check-in data available'],
    behaviorPatterns: [],
    teamContext: `Review period: ${snapshot?.period || 'Current'}`,
  };

  useEffect(() => {
    if (reviewData) {
      handleGenerateNarrative();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerateNarrative = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      const skillsLines = strengthsData.topSkills?.length
        ? strengthsData.topSkills.map(s => `- ${s.skillName} (${s.proficiency}, ${s.endorsementCount} endorsements)`).join('\n')
        : '- No skills data available';

      const prompt = `You are an HR AI assistant generating a performance review narrative for ${employee.name}.

Based on the following REAL performance data, generate a structured review narrative:

Overall Score: ${snapshot?.overallScore ?? 'N/A'}%
Period: ${snapshot?.period ?? 'Current'}

Goals Summary:
- Total: ${goalsData.total}, Completed: ${goalsData.completed}, On Track: ${goalsData.onTrack}, At Risk: ${goalsData.atRisk}, Overdue: ${goalsData.overdue}

Check-Ins:
- Total completed: ${checkInsData.total}, With content: ${checkInsData.withContent}, Avg quality: ${checkInsData.avgQuality}%

Recognition:
- Received: ${recognitionData.received}, Given: ${recognitionData.given}

Top Skills:
${skillsLines}

Format your response as JSON with these fields:
- summary: A 2-3 paragraph narrative summary based on the real data above
- goalsAchieved: Array of achieved goal descriptions based on goal data
- goalsMissed: Array of areas needing improvement based on at-risk/overdue goals
- impactHighlights: Array of impact highlights based on recognition and scores
- feedbackThemes: Array of themes based on check-in quality and recognition patterns
- behaviorPatterns: Array of behavioral patterns inferred from the data
- teamContext: A paragraph about the employee's contribution context`;

      const result = await sendChatMessage({ message: prompt, ephemeral: true });

      if (result.errors?.length) {
        throw new Error(result.errors[0]?.message || t('performanceSnapshot.narrative.generationFailed'));
      }

      if (result.payload?.message) {
        try {
          const jsonMatch = result.payload.message.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[0]) as NarrativeData;
            setNarrative(parsed);
            onNarrativeUpdate(parsed);
          } else {
            // Response wasn't JSON — use fallback narrative
            setNarrative(fallbackNarrative);
            onNarrativeUpdate(fallbackNarrative);
          }
        } catch {
          // JSON parse failed — use fallback narrative
          setNarrative(fallbackNarrative);
          onNarrativeUpdate(fallbackNarrative);
        }
      } else {
        // No response — use fallback narrative
        setNarrative(fallbackNarrative);
        onNarrativeUpdate(fallbackNarrative);
      }
    } catch (err) {
      console.error('Error generating narrative:', err);
      setError(
        err instanceof Error
          ? err.message
          : t('performanceSnapshot.narrative.unexpectedError'),
      );
      setNarrative(fallbackNarrative);
      onNarrativeUpdate(fallbackNarrative);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveEdit = () => {
    if (!narrative) return;
    const updatedNarrative = {
      ...narrative,
      summary: editedNarrative,
    };
    setNarrative(updatedNarrative);
    onNarrativeUpdate(updatedNarrative);
    setIsEditing(false);
  };

  const handleStartEdit = () => {
    setEditedNarrative(narrative?.summary || '');
    setIsEditing(true);
  };

  if (isGenerating) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
          <Sparkles size={32} className="text-purple-600" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">
          {t('performanceSnapshot.narrative.generatingTitle')}
        </h3>
        <p className="text-muted-foreground mb-4">
          {t('performanceSnapshot.narrative.generatingDescription', { name: employee.name })}
        </p>
        <div className="flex justify-center space-x-2">
          <div
            className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
            style={{ animationDelay: '0ms' }}
          />
          <div
            className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
            style={{ animationDelay: '150ms' }}
          />
          <div
            className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
            style={{ animationDelay: '300ms' }}
          />
        </div>
      </div>
    );
  }

  if (error && !narrative) {
    return (
      <div className="bg-card border border-red-300 rounded-lg p-8">
        <div className="flex items-start space-x-3">
          <AlertCircle size={24} className="text-red-500" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-red-600 mb-2">
              {t('performanceSnapshot.narrative.failedTitle')}
            </h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <button
              onClick={handleGenerateNarrative}
              className="flex items-center space-x-2 px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
            >
              <RefreshCw size={16} />
              <span>{t('performanceSnapshot.narrative.retry')}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!narrative) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <FileText size={48} className="mx-auto mb-4 opacity-50" />
        <p className="text-muted-foreground">
          {t('performanceSnapshot.narrative.noNarrative')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-foreground flex items-center space-x-2">
            <FileText size={24} />
            <span>{t('performanceSnapshot.narrative.title')}</span>
          </h3>
          <div className="flex items-center space-x-2">
            {!isEditing ? (
              <>
                <button
                  onClick={handleStartEdit}
                  className="flex items-center space-x-2 px-3 py-1.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
                >
                  <Edit size={14} />
                  <span>{t('performanceSnapshot.narrative.edit')}</span>
                </button>
                <button
                  onClick={handleGenerateNarrative}
                  className="flex items-center space-x-2 px-3 py-1.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
                >
                  <RefreshCw size={14} />
                  <span>{t('performanceSnapshot.narrative.regenerate')}</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="flex items-center space-x-2 px-3 py-1.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
                >
                  <span>{t('performanceSnapshot.narrative.cancel')}</span>
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  <Save size={14} />
                  <span>{t('performanceSnapshot.narrative.save')}</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Summary */}
        {isEditing ? (
          <textarea
            value={editedNarrative}
            onChange={(e) => setEditedNarrative(e.target.value)}
            className="w-full min-h-[200px] p-4 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            placeholder={t('performanceSnapshot.narrative.editPlaceholder')}
          />
        ) : (
          <div className="prose max-w-none">
            <p className="text-foreground leading-relaxed whitespace-pre-wrap">
              {narrative.summary}
            </p>
          </div>
        )}
      </div>

      {/* Evidence Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Goals Achieved */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Target size={18} className="text-green-500" />
            <span>{t('performanceSnapshot.narrative.goalsAchieved')}</span>
          </h4>
          <ul className="space-y-2">
            {narrative.goalsAchieved?.map((goal, index) => (
              <li key={index} className="flex items-start space-x-2">
                <CheckCircle2
                  size={16}
                  className="text-green-500 mt-0.5 flex-shrink-0"
                />
                <span className="text-sm text-foreground">{goal}</span>
              </li>
            ))}
          </ul>

          {narrative.goalsMissed?.length > 0 && (
            <>
              <h4 className="font-semibold text-foreground mt-4 mb-3 flex items-center space-x-2">
                <XCircle size={18} className="text-red-500" />
                <span>{t('performanceSnapshot.narrative.goalsMissed')}</span>
              </h4>
              <ul className="space-y-2">
                {narrative.goalsMissed.map((goal, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <AlertCircle
                      size={16}
                      className="text-red-500 mt-0.5 flex-shrink-0"
                    />
                    <span className="text-sm text-foreground">{goal}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* Impact Highlights */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Heart size={18} className="text-pink-500" />
            <span>{t('performanceSnapshot.narrative.impactHighlights')}</span>
          </h4>
          <ul className="space-y-2">
            {narrative.impactHighlights?.map((impact, index) => (
              <li key={index} className="flex items-start space-x-2">
                <Star size={16} className="text-pink-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-foreground">{impact}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Feedback Themes */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
            <MessageCircle size={18} className="text-blue-500" />
            <span>{t('performanceSnapshot.narrative.feedbackThemes')}</span>
          </h4>
          <ul className="space-y-2">
            {narrative.feedbackThemes?.map((theme, index) => (
              <li key={index} className="flex items-start space-x-2">
                <MessageCircle
                  size={16}
                  className="text-blue-500 mt-0.5 flex-shrink-0"
                />
                <span className="text-sm text-foreground">{theme}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Behavior Patterns */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
            <TrendingUp size={18} className="text-purple-500" />
            <span>{t('performanceSnapshot.narrative.behaviorPatterns')}</span>
          </h4>
          <ul className="space-y-2">
            {narrative.behaviorPatterns?.map((pattern, index) => (
              <li key={index} className="flex items-start space-x-2">
                <Activity
                  size={16}
                  className="text-purple-500 mt-0.5 flex-shrink-0"
                />
                <span className="text-sm text-foreground">{pattern}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Team Context */}
      {narrative.teamContext && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Users size={18} className="text-amber-500" />
            <span>{t('performanceSnapshot.narrative.teamContext')}</span>
          </h4>
          <p className="text-sm text-foreground leading-relaxed">
            {narrative.teamContext}
          </p>
        </div>
      )}
    </div>
  );
};

export default NarrativeCompilationPanel;
